(() => {
  const initialized = new WeakSet();
  const FLIP_DURATION_MS = 1500;
  const FLIP_STAGGER_MS = 1500;

  function initFlipSignal(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);

    const configNode = root.querySelector("[data-flip-config]");
    let config = {};

    try {
      config = JSON.parse(configNode?.textContent || "{}");
    } catch (error) {
      console.warn("Flip Signal configuration could not be read.", error);
    }

    const swatches = Array.from(root.querySelectorAll(".fs-swatch"));
    const units = Array.from(root.querySelectorAll(".fs-unit"));
    const modeCard = root.querySelector(".fs-mode-card");
    const modeLabel = root.querySelector("[data-mode-label]");
    const modeName = root.querySelector("[data-mode-name]");
    const modeKind = root.querySelector("[data-mode-kind]");
    const stageName = root.querySelector("[data-stage-name]");
    const lineup = root.querySelector("[data-lineup]");
    const signalWave = root.querySelector("[data-signal-wave]");
    const serviceToggle = root.querySelector("[data-service-toggle]");
    const serviceSmall = root.querySelector("[data-service-small]");
    const serviceText = root.querySelector("[data-service-text]");
    const customizer = root.querySelector("[data-customizer]");
    const customizerOpenButtons = root.querySelectorAll("[data-customize-open]");
    const customizerDone = root.querySelector("[data-customize-done]");
    const logoInput = root.querySelector("[data-logo-input]");
    const logoPreviews = root.querySelectorAll("[data-logo-preview]");

    let activeIndex = Number.parseInt(root.dataset.defaultIndex || "0", 10);
    let serviceActive = false;
    let flipInProgress = false;

    if (!Number.isFinite(activeIndex) || activeIndex < 0 || activeIndex >= swatches.length) {
      activeIndex = 0;
    }

    function getColorData(index) {
      const source = swatches[index] || units[index];
      return {
        color: source?.dataset.color || "#687bff",
        name: source?.dataset.name || "Cobalt Night"
      };
    }

    function renderMode() {
      const active = getColorData(activeIndex);

      if (serviceActive) {
        modeLabel.textContent = config.serviceLabel || "SERVICE SIGNAL";
        modeName.textContent = config.serviceName || "Call service";
        modeKind.textContent = config.serviceKind || "RED";
        modeCard?.classList.add("is-service");
        modeCard?.style.setProperty("--fs-lamp-color", "#ff3b30");
      } else {
        modeLabel.textContent = config.ambienceLabel || "AMBIENCE MODE";
        modeName.textContent = active.name;
        modeKind.textContent = config.moodLabel || "MOOD";
        modeCard?.classList.remove("is-service");
        modeCard?.style.setProperty("--fs-lamp-color", active.color);
      }

      if (stageName) stageName.textContent = serviceActive ? "Service Red" : active.name;
    }

    function renderServiceButton() {
      serviceToggle?.classList.toggle("is-return", serviceActive);
      if (serviceSmall) {
        serviceSmall.textContent = serviceActive
          ? config.returnSmall || "SERVICE ACTIVE"
          : config.attentionSmall || "NEED ATTENTION?";
      }
      if (serviceText) {
        serviceText.textContent = serviceActive
          ? config.returnText || "RETURN TO AMBIENCE"
          : config.attentionText || "FLIP ALL TO CALL SERVICE";
      }
    }

    function setBusy(isBusy) {
      flipInProgress = isBusy;
      root.classList.toggle("is-flipping", isBusy);

      const controlsToLock = [
        serviceToggle,
        ...swatches,
        ...units,
        ...customizerOpenButtons
      ].filter(Boolean);

      controlsToLock.forEach((control) => {
        control.disabled = isBusy;
      });

      if (serviceToggle) {
        serviceToggle.setAttribute("aria-busy", String(isBusy));
      }
    }

    function waitForFlipSequence() {
      const totalDuration =
        FLIP_DURATION_MS + Math.max(0, units.length - 1) * FLIP_STAGGER_MS + 80;

      return new Promise((resolve) => {
        window.setTimeout(resolve, totalDuration);
      });
    }

    async function runFlip(nextState) {
      if (flipInProgress || Boolean(nextState) === serviceActive) return;

      setBusy(true);
      lineup?.classList.remove("is-returning");

      if (nextState) {
        serviceActive = true;
        lineup?.classList.add("is-service");
        signalWave?.classList.add("is-active");
        renderMode();
        renderServiceButton();
      } else {
        lineup?.classList.remove("is-service");
        lineup?.classList.add("is-returning");
        signalWave?.classList.remove("is-active");
      }

      await waitForFlipSequence();

      if (!nextState) {
        lineup?.classList.remove("is-returning");
        serviceActive = false;
        renderMode();
        renderServiceButton();
      }

      setBusy(false);
    }

    async function selectColor(index) {
      if (flipInProgress || index < 0 || index >= swatches.length) return;

      if (serviceActive) {
        await runFlip(false);
      }

      activeIndex = index;

      swatches.forEach((swatch, itemIndex) => {
        const selected = itemIndex === activeIndex;
        swatch.classList.toggle("is-selected", selected);
        swatch.setAttribute("aria-pressed", String(selected));
      });

      units.forEach((unit, itemIndex) => {
        const selected = itemIndex === activeIndex;
        unit.classList.toggle("is-selected", selected);
        unit.setAttribute("aria-pressed", String(selected));
      });

      renderMode();
    }

    function openCustomizer() {
      customizer?.classList.add("is-open");
      window.requestAnimationFrame(() => logoInput?.focus());
    }

    function closeCustomizer() {
      customizer?.classList.remove("is-open");
    }

    function updateLogo() {
      const logo = (logoInput?.value || "").trim() || "YOUR LOGO";
      logoPreviews.forEach((preview) => {
        preview.textContent = logo;
      });
    }

    swatches.forEach((swatch, index) => {
      swatch.addEventListener("click", () => selectColor(index));
    });

    units.forEach((unit, index) => {
      unit.addEventListener("click", () => selectColor(index));
    });

    serviceToggle?.addEventListener("click", () => {
      void runFlip(!serviceActive);
    });
    customizerOpenButtons.forEach((button) => button.addEventListener("click", openCustomizer));
    customizerDone?.addEventListener("click", closeCustomizer);
    logoInput?.addEventListener("input", updateLogo);
    logoInput?.addEventListener("keydown", (event) => {
      if (event.key === "Enter") closeCustomizer();
    });

    selectColor(activeIndex);
    updateLogo();
    renderServiceButton();
  }

  function initAll(container = document) {
    container.querySelectorAll("[data-flip-signal]").forEach(initFlipSignal);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
